import { useState, useEffect, useRef, useCallback } from 'react';
import { auth, createAuditLog } from '../../../lib/firebase';
import { usePath, navigate, parseRoute } from '../../../lib/router';
import { UserProfile, AuditLog } from '../../../types';

export function useUsers(options: {
  adminEmail: string;
  setAuditLogs: (logs: AuditLog[]) => void;
  auditLogs: AuditLog[];
}) {
  const { adminEmail, setAuditLogs, auditLogs } = options;
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isBlockingUser, setIsBlockingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  const isClosingUserRef = useRef(false);

  const handleSelectUser = useCallback((user: UserProfile | null) => {
    if (!user) {
      isClosingUserRef.current = true;
      setSelectedUser(null);
      if (parseRoute(window.location.pathname).type === 'users_detail') {
        navigate('/users');
      }
      setTimeout(() => { isClosingUserRef.current = false; }, 400);
    } else {
      isClosingUserRef.current = false;
      setSelectedUser(user);
      navigate(`/users/adminview/${user.uid}`);
    }
  }, []);

  // Sync selectedUser with URL path
  useEffect(() => {
    if (isClosingUserRef.current) return;
    if (currentRoute.type === 'users_detail') {
      const targetId = currentRoute.id;
      if (selectedUser?.uid !== targetId) {
        const found = allUsers.find(u => u.uid === targetId);
        if (found) {
          setSelectedUser(found);
        } else if (!usersLoading && allUsers.length > 0) {
          setSelectedUser(null);
        }
      }
    } else if (currentRoute.type !== ('users_detail' as any) && selectedUser) {
      setSelectedUser(null);
    }
  }, [currentPath, currentRoute.type, currentRoute.id, allUsers, usersLoading, selectedUser?.uid]);

  const prevSelectedUserRef = useRef(selectedUser);

  // Sync URL path when selectedUser changes
  useEffect(() => {
    if (selectedUser?.uid) {
      if (currentRoute.type !== 'users_detail' || currentRoute.id !== selectedUser.uid) {
        navigate(`/users/adminview/${selectedUser.uid}`);
      }
    } else if (prevSelectedUserRef.current && !selectedUser && currentRoute.type === 'users_detail') {
      navigate('/users');
    }
    prevSelectedUserRef.current = selectedUser;
  }, [selectedUser?.uid, currentRoute.type, currentRoute.id]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/users', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setAllUsers(data.users || []);
    } catch (err) {
      console.warn("Could not fetch users:", err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleToggleBlockUser = async (user: UserProfile) => {
    if (isBlockingUser) return;
    setIsBlockingUser(true);
    try {
      const currentBlocked = user.blocked ?? false;
      // PT7-004: Call server endpoint which also disables Firebase Auth account
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/block-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ uid: user.uid, blocked: !currentBlocked }),
      });
      if (!res.ok) {
        const err = await res.text();
        console.error('Block-user API error:', err);
        return;
      }
      setAllUsers(prev => prev.map(u => u.uid === user.uid ? {
        ...u,
        blocked: !currentBlocked,
        blockedAt: !currentBlocked ? new Date().toISOString() : undefined
      } : u));
      if (selectedUser?.uid === user.uid) {
        setSelectedUser(prev => prev ? { ...prev, blocked: !currentBlocked, blockedAt: !currentBlocked ? new Date().toISOString() : undefined } : null);
      }
    } catch (err) {
      console.error("Failed to toggle block status:", err);
    } finally {
      setIsBlockingUser(false);
    }
  };

  const createUser = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to create user' };
      await fetchUsers();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const sendPromotionCode = async (targetUid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/promote-send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ targetUid }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to send code' };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const verifyPromotionCode = async (targetUid: string, code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/promote-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ targetUid, code }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Verification failed' };
      await fetchUsers();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  // ── Role change: send verification code (for admin/employer promotion) ──
  const sendRoleChangeCode = async (targetUid: string, newRole: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/role-change-send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ targetUid, newRole }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to send code' };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  // ── Role change: verify code and apply (for admin/employer promotion) ───
  const verifyRoleChangeCode = async (targetUid: string, code: string, newRole: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/role-change-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ targetUid, code, newRole }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Verification failed' };
      await fetchUsers();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  // ── Role change: direct (for demote to applicant) ──────────────────────
  const changeRoleDirect = async (targetUid: string, newRole: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/change-role-direct', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ targetUid, newRole }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to change role' };
      await fetchUsers();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  // ── Promote to employer (links user to employer company) ───────────────
  const promoteToEmployer = async (uid: string, employerId: string, email?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/users/promote-to-employer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ uid, employerId, email: email || '' }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to promote to employer' };
      await fetchUsers();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  // ── Fetch available employer companies for the selector ─────────────────
  const fetchEmployersList = async (): Promise<{ id: string; companyName: string }[]> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/employers', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.employers || []).map((e: any) => ({ id: e.id, companyName: e.companyName }));
    } catch { return []; }
  };

  return {
    allUsers,
    usersLoading,
    isBlockingUser,
    selectedUser,
    setSelectedUser: handleSelectUser,
    setAllUsers,
    setUsersLoading,
    setAuditLogs,
    fetchUsers,
    handleToggleBlockUser,
    createUser,
    sendPromotionCode,
    verifyPromotionCode,
    sendRoleChangeCode,
    verifyRoleChangeCode,
    changeRoleDirect,
    promoteToEmployer,
    fetchEmployersList,
    createAuditLog,
  };
}
